import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSortable from '../hooks/useSortable';
import SortableHeader from '../components/SortableHeader';
import UiDateField from '../components/ui/UiDateField';
import {
  Plus, Search, Edit2, Trash2, Package, AlertTriangle,
  TrendingUp, TrendingDown, X, Upload, History, UtensilsCrossed, Calendar, Link2, ChevronLeft, ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import VoiceInput from '../components/VoiceInput';
import Wastage from './Wastage';
import InventoryTab from './inventory/InventoryTab';
import { formatApiError } from '../utils/apiErrors';
import { buildStandardPresetOptions, shiftDateRangeByPreset } from '../utils/dateRangeUi';
import { formatUiCurrencyCompact, formatUiDate, formatUiDateTime, formatUiDateTimeRange } from '../utils/uiFormat';

const INVENTORY_STATES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'perishable', label: 'Perishable' },
  { value: 'suspended', label: 'Suspended' },
];

const INVENTORY_TABS = [
  { value: 'inventory', label: 'Inventory', permissionModule: 'inventory' },
  { value: 'wastage', label: 'Wastage', permissionModule: 'wastage' },
];

const getFiveDayExpiry = () => {
  const date = new Date();
  date.setDate(date.getDate() + 5);
  return date.toISOString().slice(0, 10);
};

const getTodayValue = () => new Date().toISOString().slice(0, 10);
const addCalendarDays = (dateStr, days) => {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};
const formatDate = formatUiDate;

const Inventory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [rawMaterials, setRawMaterials] = useState([]);
  const [units, setUnits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPurchaseHistoryModal, setShowPurchaseHistoryModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [pendingInventoryItems, setPendingInventoryItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [adjustmentHistoryLoading, setAdjustmentHistoryLoading] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [historyPreset, setHistoryPreset] = useState('month');
  const [historyFrom, setHistoryFrom] = useState('');
  const [historyTo, setHistoryTo] = useState('');
  const [historyRangeOverride, setHistoryRangeOverride] = useState(null);
  const [filter, setFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '', unit: '', department: '', quantity: 0, min_stock_level: 10,
    purchase_price: 0, supplier: '', state: 'active', expiry_date: '', description: ''
  });
  const [adjustData, setAdjustData] = useState({ id: null, quantity: 0, reason: '', action_type: 'adjustment' });
  const [showUsedInModal, setShowUsedInModal] = useState(false);
  const [usedInData, setUsedInData] = useState({ name: '', items: [] });

  const { hasPermission } = useAuthStore();
  const canViewInventory = hasPermission('inventory', 'view');
  const canViewWastage = hasPermission('wastage', 'view');
  const availableTabs = useMemo(() => INVENTORY_TABS.filter((tab) => (
    tab.permissionModule === 'inventory' ? canViewInventory : canViewWastage
  )), [canViewInventory, canViewWastage]);
  const requestedTab = new URLSearchParams(location.search).get('tab');
  const activeTab = availableTabs.some((tab) => tab.value === requestedTab)
    ? requestedTab
    : (availableTabs[0]?.value || 'inventory');

  const setActiveTab = (nextTab) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', nextTab);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  useEffect(() => {
    if (!availableTabs.length || requestedTab === activeTab) return;
    const params = new URLSearchParams(location.search);
    params.set('tab', activeTab);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  }, [activeTab, availableTabs.length, location.pathname, location.search, navigate, requestedTab]);

  useEffect(() => {
    if (!canViewInventory || activeTab !== 'inventory') {
      setLoading(false);
      return;
    }
    fetchData();
  }, [activeTab, canViewInventory]);

  useEffect(() => {
    if (activeTab !== 'inventory') return;
    const params = new URLSearchParams(location.search);
    const search = params.get('search');
    const itemId = params.get('item_id');
    if (search) {
      setSearchQuery(search);
      return;
    }
    if (itemId && rawMaterials.length) {
      const matched = rawMaterials.find(item => String(item.id) === String(itemId));
      if (matched) setSearchQuery(matched.name || '');
    }
  }, [location.search, rawMaterials]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [materialsRes, unitsRes, suppliersRes, deptsRes] = await Promise.all([
        api.get('/raw-materials/?page_size=500'),
        api.get('/units/?page_size=500'),
        api.get('/suppliers/?active=true&page_size=500'),
        api.get('/departments/?page_size=500')
      ]);
      setRawMaterials(materialsRes.data.results || materialsRes.data);
      setUnits(unitsRes.data.results || unitsRes.data);
      setSuppliers(suppliersRes.data.results || suppliersRes.data);
      setDepartments(deptsRes.data.results || deptsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(formatApiError(error, 'Failed to load inventory data'));
    } finally {
      setLoading(false);
    }
  };

  const { sortKey: iSortKey, sortDir: iSortDir, handleSort: iHandleSort, sortData: iSortData } = useSortable('name', 'asc');
  const { sortKey: hSortKey, sortDir: hSortDir, handleSort: hHandleSort, sortData: hSortData } = useSortable('created_at', 'desc');
  const { sortKey: pSortKey, sortDir: pSortDir, handleSort: pHandleSort, sortData: pSortData } = useSortable('date', 'desc');
  const { sortKey: aSortKey, sortDir: aSortDir, handleSort: aHandleSort, sortData: aSortData } = useSortable('created_at', 'desc');

  const filteredMaterials = iSortData(rawMaterials.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDept = !departmentFilter || String(item.department) === departmentFilter;
    const matchesSupplier = !supplierFilter || String(item.supplier) === supplierFilter;
    if (filter === 'low_stock') return matchesSearch && matchesDept && matchesSupplier && item.is_low_stock;
    if (filter === 'expiring') return matchesSearch && matchesDept && matchesSupplier && item.is_expiring_soon;
    return matchesSearch && matchesDept && matchesSupplier;
  }), (item, sortKey) => {
    if (sortKey === 'purchase_price') return getCurrentUnitPrice(item);
    if (sortKey === 'inventory_value') return Math.max(0, parseFloat(item.quantity) * getCurrentUnitPrice(item));
    if (sortKey === 'last_purchase_date') return getLastPurchasedDate(item);
    return item?.[sortKey];
  });

  const buildInventoryEntry = (sourceForm = formData) => {
    if (!String(sourceForm.name || '').trim()) { toast.error('Enter item name'); return null; }
    if (!sourceForm.unit) { toast.error('Select unit'); return null; }
    return {
      _key: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      label: sourceForm.name,
      quantity: sourceForm.quantity,
      data: { ...sourceForm },
    };
  };

  const handleApplyInventoryPending = () => {
    const entry = buildInventoryEntry();
    if (!entry) return;
    setPendingInventoryItems(prev => [...prev, entry]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const entries = selectedItem
      ? [buildInventoryEntry()]
      : (pendingInventoryItems.length > 0 ? pendingInventoryItems : [buildInventoryEntry()]);
    if (entries.some(entry => !entry)) return;
    try {
      if (selectedItem) {
        await api.patch(`/raw-materials/${selectedItem.id}/`, entries[0].data);
        toast.success('Item updated successfully');
      } else {
        // One atomic request — all items save together or none do.
        await api.post('/raw-materials/bulk-save/', { entries: entries.map(entry => entry.data) });
        toast.success(`${entries.length} item${entries.length === 1 ? '' : 's'} added successfully`);
      }
      setPendingInventoryItems([]);
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save item'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`/raw-materials/${id}/`);
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete item'));
    }
  };

  const handleAdjust = async () => {
    try {
      if (adjustData.id) {
        await api.patch(`/inventory-history/${adjustData.id}/`, {
          quantity_change: adjustData.quantity,
          reason: adjustData.reason,
        });
        toast.success('Adjustment updated successfully');
      } else {
        await api.post(`/raw-materials/${selectedItem.id}/adjust_stock/`, adjustData);
        toast.success('Stock adjusted successfully');
      }
      setAdjustData({ id: null, quantity: 0, reason: '', action_type: 'adjustment' });
      await Promise.all([fetchData(), fetchAdjustmentHistory(selectedItem)]);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to adjust stock'));
    }
  };

  const fetchAdjustmentHistory = async (item) => {
    if (!item?.id) return;
    try {
      setAdjustmentHistoryLoading(true);
      const res = await api.get(`/inventory-history/?raw_material_id=${item.id}&action=adjustment&page_size=5000`);
      setAdjustmentHistory(res.data.results || res.data);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load adjustment history'));
    } finally {
      setAdjustmentHistoryLoading(false);
    }
  };

  const openAdjustModal = async (item) => {
    setSelectedItem(item);
    setAdjustData({ id: null, quantity: 0, reason: '', action_type: 'adjustment' });
    setShowAdjustModal(true);
    await fetchAdjustmentHistory(item);
  };

  const handleEditAdjustmentEntry = (entry) => {
    setAdjustData({
      id: entry.id,
      quantity: entry.quantity_change,
      reason: entry.reason || '',
      action_type: entry.action || 'adjustment',
    });
  };

  const handleDeleteAdjustmentEntry = async (entry) => {
    if (!window.confirm('Delete this adjustment entry?')) return;
    try {
      await api.delete(`/inventory-history/${entry.id}/`);
      toast.success('Adjustment deleted successfully');
      setAdjustData((prev) => (prev.id === entry.id ? { id: null, quantity: 0, reason: '', action_type: 'adjustment' } : prev));
      await Promise.all([fetchData(), fetchAdjustmentHistory(selectedItem)]);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete adjustment'));
    }
  };

  const fetchHistory = async (item) => {
    try {
      const res = await api.get(`/inventory-history/?raw_material_id=${item.id}&action=sale&page_size=5000&ordering=-created_at`);
      setHistory(res.data.results || res.data);
      setSelectedItem(item);
      setHistoryPreset('month');
      setHistoryFrom('');
      setHistoryTo('');
      setShowHistoryModal(true);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load history'));
    }
  };

  const openPurchaseEntry = (item, purchaseRow) => {
    setShowPurchaseHistoryModal(false);
    const params = new URLSearchParams({
      tab: 'purchases',
      view: 'table',
      item_id: String(item.id),
    });
    if (purchaseRow?.date) {
      params.set('start_date', purchaseRow.date);
      params.set('end_date', purchaseRow.date);
    }
    if (purchaseRow?.expense_number) {
      params.set('search', purchaseRow.expense_number);
      params.set('expense_number', purchaseRow.expense_number);
    }
    else if (item?.name) params.set('search', item.name);
    navigate(`/expenses?${params.toString()}`);
  };

  const fetchPurchaseHistory = async (item) => {
    try {
      const res = await api.get('/reports/drill-down/', {
        params: {
          type: 'inventory_purchase_history',
          raw_material_id: item.id,
          start_date: '2000-01-01',
          end_date: getTodayValue(),
        },
      });
      setSelectedItem(item);
      setPurchaseHistory(res.data?.data || []);
      setShowPurchaseHistoryModal(true);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load purchase history'));
    }
  };

  /* Legacy history filter snapshot kept local during the standardization pass.
  const legacyHistoryDateRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fmt = (d) => d.toISOString().slice(0, 10);
    if (historyPreset === 'month') return { from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt(new Date(today.getFullYear(), today.getMonth() + 1, 0)) };
    if (historyPreset === 'year') return { from: fmt(new Date(today.getFullYear(), 0, 1)), to: fmt(new Date(today.getFullYear(), 11, 31)) };
    if (historyPreset === 'custom') return { from: historyFrom ? formatDate(historyFrom) : '—', to: historyTo ? formatDate(historyTo) : '—' };
    return null;
  }, [historyPreset, historyFrom, historyTo]);

  const legacyFilteredHistory = useMemo(() => {
    if (!history.length) return [];
    if (historyPreset === 'all') return history;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let from = '', to = today.toISOString().slice(0, 10);
    if (historyPreset === 'today') { from = to; }
    else if (historyPreset === 'week') { const d = new Date(today); d.setDate(d.getDate() - 6); from = d.toISOString().slice(0, 10); }
    else if (historyPreset === 'month') { from = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`; }
    else if (historyPreset === 'year') { from = `${today.getFullYear()}-01-01`; }
    else if (historyPreset === 'custom') { from = historyFrom; to = historyTo; }
    return history.filter(h => {
      const d = h.created_at.slice(0, 10);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [history, historyPreset, historyFrom, historyTo]);
  */

  const historyDateRange = useMemo(() => {
    if (historyRangeOverride) return historyRangeOverride;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fmt = (d) => d.toISOString().slice(0, 10);
    if (historyPreset === 'month') {
      return {
        from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: fmt(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    }
    if (historyPreset === 'year') {
      return {
        from: fmt(new Date(today.getFullYear(), 0, 1)),
        to: fmt(new Date(today.getFullYear(), 11, 31)),
      };
    }
    if (historyPreset === 'custom') return { from: historyFrom || '', to: historyTo || '' };
    return null;
  }, [historyPreset, historyFrom, historyTo, historyRangeOverride]);

  const filteredHistory = useMemo(() => {
    if (!history.length) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let from = '';
    let to = '';
    if (historyRangeOverride) {
      from = historyRangeOverride.from;
      to = historyRangeOverride.to;
    } else if (historyPreset === 'month') {
      from = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
      to = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
    } else if (historyPreset === 'year') {
      from = `${today.getFullYear()}-01-01`;
      to = `${today.getFullYear()}-12-31`;
    } else if (historyPreset === 'custom') {
      from = historyFrom;
      to = historyTo;
    }
    return history.filter((entry) => {
      const dateValue = entry.created_at.slice(0, 10);
      if (from && dateValue < from) return false;
      if (to && dateValue > to) return false;
      return true;
    });
  }, [history, historyPreset, historyFrom, historyTo, historyRangeOverride]);

  const fetchUsedIn = async (item) => {
    try {
      const res = await api.get(`/raw-materials/${item.id}/used_in/`);
      setUsedInData({ name: res.data.raw_material, items: res.data.used_in || [] });
      setShowUsedInModal(true);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load usage data'));
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setPendingInventoryItems([]);
    setFormData({
      name: item.name,
      unit: item.unit,
      department: item.department || '',
      quantity: item.quantity,
      min_stock_level: item.min_stock_level,
      purchase_price: item.purchase_price,
      supplier: item.supplier || '',
      state: item.state || 'active',
      expiry_date: item.expiry_date || '',
      description: item.description || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setSelectedItem(null);
    setFormData({
      name: '', unit: '', department: '', quantity: 0, min_stock_level: 10,
      purchase_price: 0, supplier: '', state: 'active', expiry_date: '', description: ''
    });
  };

  const closeInventoryModal = () => {
    setShowModal(false);
    setPendingInventoryItems([]);
    resetForm();
  };

  const getCurrentUnitPrice = (item) => parseFloat(item.current_purchase_price ?? item.purchase_price ?? 0);
  const getLastPurchasedDate = (item) => item?.last_purchase_date || null;
  // Summary reflects the current search + department + supplier scope (not the
  // low-stock/expiring chip, so those counts stay meaningful within that scope).
  const summaryMaterials = rawMaterials.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDept = !departmentFilter || String(item.department) === departmentFilter;
    const matchesSupplier = !supplierFilter || String(item.supplier) === supplierFilter;
    return matchesSearch && matchesDept && matchesSupplier;
  });
  const totalItemsCount = summaryMaterials.length;
  const lowStockCount = summaryMaterials.filter(m => m.is_low_stock).length;
  const expiringCount = summaryMaterials.filter(m => m.is_expiring_soon).length;
  const totalValue = summaryMaterials.reduce((sum, m) => sum + (parseFloat(m.quantity) * getCurrentUnitPrice(m)), 0);
  const formatAmount = (amount) => formatUiCurrencyCompact(amount);
  const sortedHistory = useMemo(
    () => hSortData(filteredHistory, (entry, sortKey) => entry?.[sortKey]),
    [filteredHistory, hSortData]
  );
  const sortedPurchaseHistory = useMemo(
    () => pSortData(purchaseHistory, (entry, sortKey) => entry?.[sortKey]),
    [purchaseHistory, pSortData]
  );
  const sortedAdjustmentHistory = useMemo(
    () => aSortData(adjustmentHistory, (entry, sortKey) => entry?.[sortKey]),
    [adjustmentHistory, aSortData]
  );

  const handleHistoryPresetChange = (preset) => {
    setHistoryPreset(preset);
    setHistoryRangeOverride(null);
    if (preset === 'custom') {
      setHistoryFrom('');
      setHistoryTo('');
    }
  };

  const shiftHistoryRange = (delta) => {
    if (!historyDateRange?.from || !historyDateRange?.to) return;
    const shifted = shiftDateRangeByPreset({
      preset: historyPreset,
      startDate: historyDateRange.from,
      endDate: historyDateRange.to,
      delta,
    });
    setHistoryRangeOverride({ from: shifted.startDate, to: shifted.endDate });
    if (historyPreset === 'custom') {
      setHistoryFrom(shifted.startDate);
      setHistoryTo(shifted.endDate);
    }
  };

  useEffect(() => {
    if (formData.state === 'perishable' && !formData.expiry_date) {
      setFormData(prev => ({ ...prev, expiry_date: getFiveDayExpiry() }));
    }
  }, [formData.state, formData.expiry_date]);

  if (activeTab === 'inventory' && loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const inventoryCtx = {
    aHandleSort,
    aSortDir,
    aSortKey,
    addCalendarDays,
    adjustData,
    adjustmentHistoryLoading,
    closeInventoryModal,
    departmentFilter,
    departments,
    expiringCount,
    totalItemsCount,
    fetchHistory,
    fetchPurchaseHistory,
    fetchUsedIn,
    filter,
    filteredHistory,
    filteredMaterials,
    formData,
    formatAmount,
    formatDate,
    getCurrentUnitPrice,
    getFiveDayExpiry,
    getLastPurchasedDate,
    hHandleSort,
    hSortDir,
    hSortKey,
    handleAdjust,
    handleApplyInventoryPending,
    handleDelete,
    handleDeleteAdjustmentEntry,
    handleEditAdjustmentEntry,
    handleHistoryPresetChange,
    handleSubmit,
    hasPermission,
    history,
    historyDateRange,
    historyFrom,
    historyPreset,
    historyTo,
    iHandleSort,
    iSortDir,
    iSortKey,
    lowStockCount,
    openAdjustModal,
    openEditModal,
    openPurchaseEntry,
    pHandleSort,
    pSortDir,
    pSortKey,
    pendingInventoryItems,
    purchaseHistory,
    rawMaterials,
    searchQuery,
    selectedItem,
    setAdjustData,
    setDepartmentFilter,
    setFilter,
    setFormData,
    setHistoryFrom,
    setHistoryRangeOverride,
    setHistoryTo,
    setPendingInventoryItems,
    setSearchQuery,
    setShowAdjustModal,
    setShowHistoryModal,
    setShowPurchaseHistoryModal,
    setShowUsedInModal,
    setSupplierFilter,
    shiftHistoryRange,
    showAdjustModal,
    showHistoryModal,
    showModal,
    showPurchaseHistoryModal,
    showUsedInModal,
    sortedAdjustmentHistory,
    sortedHistory,
    sortedPurchaseHistory,
    supplierFilter,
    suppliers,
    totalValue,
    units,
    usedInData,
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Inventory Management</h1>
          <p className="text-sm sm:text-base text-gray-500">
            {activeTab === 'wastage' ? 'Track and manage wastage entries' : 'Manage raw materials and stock levels'}
          </p>
        </div>
        {activeTab === 'inventory' && hasPermission('inventory', 'add') && (
          <button
            onClick={() => { setPendingInventoryItems([]); resetForm(); setShowModal(true); }}
            className="bg-primary-600 hover:bg-primary-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 text-sm sm:text-base"
          >
            Add Item
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm p-2">
        <div className="flex flex-wrap gap-2">
          {availableTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === tab.value
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'wastage' ? (
        <Wastage embedded />
      ) : (
        <InventoryTab {...inventoryCtx} />
      )}
    </div>
  );
};

export default Inventory;
